'use client';

import { CheckCircleIcon } from '@heroicons/react/24/solid'; // For checkmark
import { useEffect, useState } from 'react';

// --- New SVG Looping Circle Spinner ---
const CircleLoopSpinner = () => (
  <svg
    className="animate-spin h-4 w-4 text-zinc-600 flex-shrink-0" // Add flex-shrink-0
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
  >
    <circle
      className="opacity-25" // Fainter background circle track
      cx="12"
      cy="12"
      r="10"
      stroke="currentColor"
      strokeWidth="4"
    ></circle>
    <path
      className="opacity-75" // More opaque spinning arc
      fill="currentColor"
      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
    ></path>
  </svg>
);
// --- End New Spinner ---

// Simple Spinner Component (can be customized)
// const MiniSpinner = () => (
//   <div className="w-4 h-4 border-2 border-zinc-400 border-t-transparent rounded-full animate-spin"></div>
// ); // Keep commented out or remove

interface ReasoningLoaderProps {
  dictionary: any;
  city: string;
  propertyType: string;
  livingArea: number;
  bedrooms: number;
  bathrooms: number;
  plotArea?: number; // Optional for plot area
  condition: string;
  epcValue: string;
  asbestos: string;
  finishLevel: string;
  additionalDetails?: string | null;
  currentAvmWorkflowStep?: string; // <-- Add this new prop
}

export default function ReasoningLoader({ 
  dictionary,
  city,
  propertyType,
  livingArea,
  bedrooms,
  bathrooms,
  plotArea,
  condition,
  epcValue,
  asbestos,
  finishLevel,
  additionalDetails,
  currentAvmWorkflowStep,
}: ReasoningLoaderProps) {
  const [step, setStep] = useState(0); // 0: initial, 1: step1 done, 2: step2 done, 3: step3 done, 4: step4 done (if exists)

  // Calculate the number of steps based on whether additional details are provided
  const hasAdditionalDetails = additionalDetails && additionalDetails.trim().length > 0;
  const totalSteps = hasAdditionalDetails ? 4 : 3;

  useEffect(() => {
    const timers: NodeJS.Timeout[] = [];
    // Start step 1 after a very short delay (optional, feels smoother)
    // timers.push(setTimeout(() => setStep(1), 100)); 
    // Set subsequent steps with 1.5s intervals
    for (let i = 1; i <= totalSteps; i++) {
      timers.push(
        setTimeout(() => {
          setStep(i);
        }, i * 1500) // Each step appears 1.5 seconds after the previous (relative to t=0)
      );
    }

    return () => {
      timers.forEach(clearTimeout);
    };
  }, [totalSteps]); // Re-run if totalSteps changes (though it shouldn't after init)

  const searchingText = (dictionary?.sellingForm?.reasoningSearching || 'Searching in <strong>[MUNICIPALITY]</strong>...')
    .replace('[MUNICIPALITY]', city);
  
  const narrowingDetails = [
    `${propertyType}`, // Start with property type
    `~${livingArea} m²` // Add living area
  ];
  if (typeof bedrooms === 'number') {
    narrowingDetails.push(`${bedrooms} ${bedrooms === 1 ? (dictionary?.sellingForm?.bedroomSingular || 'bed') : (dictionary?.sellingForm?.bedroomPlural || 'beds')}`);
  }
  if (typeof bathrooms === 'number') {
    narrowingDetails.push(`${bathrooms} ${bathrooms === 1 ? (dictionary?.sellingForm?.bathroomSingular || 'bath') : (dictionary?.sellingForm?.bathroomPlural || 'baths')}`);
  }
  if (propertyType === (dictionary?.sellingForm?.propertyTypeHouse || 'house') && typeof plotArea === 'number' && plotArea > 0) {
    narrowingDetails.push(`${plotArea} m² ${dictionary?.sellingForm?.reasoningPlotArea || 'plot'}`);
  }

  const narrowingTextTemplate = dictionary?.sellingForm?.reasoningNarrowing || 'Narrowing to [DETAILS]...';
  const narrowingText = narrowingTextTemplate.replace('[DETAILS]', narrowingDetails.join(', '));

  const conditionEpcDetails = [];
  const conditionKey = `condition${condition}`;
  const translatedCondition = dictionary?.propertyForm?.[conditionKey] || condition;
  if (translatedCondition) {
    conditionEpcDetails.push(translatedCondition);
  }

  if (finishLevel) {
    const finishLevelKeyMap: { [key: string]: string } = {
      'poor': 'finishLevelPoor',
      'moderate': 'finishLevelModerate',
      'good': 'finishLevelGood',
      'very_good': 'finishLevelVeryGood',
    };
    const finishLevelKey = finishLevelKeyMap[finishLevel];
    const translatedFinishLevel = dictionary?.sellingForm?.[finishLevelKey] || finishLevel;
    conditionEpcDetails.push(translatedFinishLevel);
  }

  if (epcValue && epcValue.toLowerCase() !== 'unknown') {
    const epcKey = `propertyEpcLabel${epcValue.toUpperCase()}`;
    const translatedEpc = dictionary?.sellingForm?.[epcKey] || `EPC ${epcValue}`;
    conditionEpcDetails.push(translatedEpc);
  }

  if (asbestos) {
    const asbestosKeyMap: { [key: string]: string } = {
      'none': 'asbestosNone',
      'not_much': 'asbestosNotMuch',
      'lots': 'asbestosLots',
      'dont_know': 'asbestosDontKnow',
    };
    const asbestosKey = asbestosKeyMap[asbestos];
    const translatedAsbestosValue = dictionary?.sellingForm?.[asbestosKey] || asbestos;
    const asbestosLabel = dictionary?.sellingForm?.asbestos || 'Asbestos';
    conditionEpcDetails.push(`${asbestosLabel}: ${translatedAsbestosValue}`);
  }

  const conditionEpcTemplate = dictionary?.sellingForm?.reasoningConditionEpc || 'Checking condition [DETAILS]...';
  const conditionEpcText = conditionEpcTemplate.replace('[DETAILS]', conditionEpcDetails.join(', '));

  // --- Add logic for the 4th step --- 
  let additionalInfoText = '';
  if (hasAdditionalDetails) {
    const additionalInfoTemplate = dictionary?.sellingForm?.reasoningAdditionalInfo || 'Considering extra details: <strong>[DETAILS]</strong>...';
    // Basic truncation if details are too long
    const truncatedDetails = additionalDetails.length > 50 ? `${additionalDetails.substring(0, 47)}...` : additionalDetails;
    additionalInfoText = additionalInfoTemplate.replace('[DETAILS]', truncatedDetails);
  }
  // --- End 4th step logic --- 

  return (
    // Outer container with left padding for the border
    <div className="pl-4 relative py-2"> 
      {/* Vertical Line */}
      <div className="absolute left-0 top-0 bottom-0 w-0.5 bg-zinc-300"></div>
      
      {/* Steps Container */}
      <div className="space-y-1.5">
        {/* Step 1: Searching */}
        <div className="flex items-center gap-2 text-base md:text-sm text-zinc-600">
          {step >= 1 ? (
            <CheckCircleIcon className="w-4 h-4 text-green-500 flex-shrink-0" />
          ) : (
            <CircleLoopSpinner /> // Use new spinner
          )}
          <span dangerouslySetInnerHTML={{ __html: searchingText }} />
        </div>

        {/* Step 2: Narrowing (only show if step >= 1) */}
        {step >= 1 && (
          <div className="flex items-center gap-2 text-base md:text-sm text-zinc-600">
            {step >= 2 ? (
              <CheckCircleIcon className="w-4 h-4 text-green-500 flex-shrink-0" />
            ) : (
              <CircleLoopSpinner /> // Use new spinner
            )}
            <span dangerouslySetInnerHTML={{ __html: narrowingText }} />
          </div>
        )}

        {/* Step 3: Condition/EPC (only show if step >= 2) */}
        {step >= 2 && (
          <div className="flex items-center gap-2 text-base md:text-sm text-zinc-600">
            {( // Checkmark logic for Step 3
              // If it IS the final step (totalSteps === 3), check if parent workflow step is BEYOND step 5
              (totalSteps === 3 && currentAvmWorkflowStep !== 'sales-avm-step5-additional-info') ||
              // If it's NOT the final step (totalSteps === 4), check internal timer
              (totalSteps === 4 && step >= 3)
            ) ? (
                <CheckCircleIcon className="w-4 h-4 text-green-500 flex-shrink-0" />
              ) : (
                <CircleLoopSpinner /> // Use new spinner
              )}
            <span dangerouslySetInnerHTML={{ __html: conditionEpcText }} />
          </div>
        )}

        {/* Step 4: Additional Info (only show if step >= 3 AND hasAdditionalDetails) */}
        {/* This is always the final step if shown (totalSteps === 4) */}
        {step >= 3 && hasAdditionalDetails && (
          <div className="flex items-center gap-2 text-base md:text-sm text-zinc-600">
            {/* Show checkmark ONLY if parent workflow step is BEYOND step 5 */}
            {currentAvmWorkflowStep !== 'sales-avm-step5-additional-info' ? (
              <CheckCircleIcon className="w-4 h-4 text-green-500 flex-shrink-0" />
            ) : (
              <CircleLoopSpinner /> // Use new spinner
            )}
            <span dangerouslySetInnerHTML={{ __html: additionalInfoText }} />
          </div>
        )}
      </div>
    </div>
  );
} 